import React, { useRef } from 'react';
import { AiOutlineFileAdd } from 'react-icons/ai';
import { MdClose } from 'react-icons/md';
import { RiFileTextFill } from 'react-icons/ri';
import { MdVideoLibrary } from 'react-icons/md';
import { UploadedFile } from '../../../api/models';
import { DOCUMENT_TYPES_LABEL, KNOWLEDGE_ACCEPT } from '../../../constants/fileUpload';

interface FileUploadAreaProps {
    uploadedFiles: UploadedFile[];
    isDragOver: boolean;
    onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
    onDragLeave: () => void;
    onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onRemoveFile: (index: number) => void;
    documentSizeLimit: number; // in MB
    videoSizeLimitGb: number; // in GB
    fileLimit: number;
    isDisabled?: boolean;
}

export const FileUploadArea: React.FC<FileUploadAreaProps> = ({
    uploadedFiles,
    isDragOver,
    onDragOver,
    onDragLeave,
    onDrop,
    onFileChange,
    onRemoveFile,
    documentSizeLimit,
    videoSizeLimitGb,
    fileLimit,
    isDisabled = false
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);

    const formatFileSize = (size: number) => {
        if (size >= 1024 * 1024 * 1024) {
            return (size / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
        } else if (size >= 1024 * 1024) {
            return (size / (1024 * 1024)).toFixed(2) + ' MB';
        } else if (size >= 1024) {
            return (size / 1024).toFixed(2) + ' KB';
        } else {
            return size + ' B';
        }
    };

    const documentCount = uploadedFiles.filter(uf => uf.type === 'document').length;
    const videoCount = uploadedFiles.filter(uf => uf.type === 'video').length;

    return (
        <div className={`transition-opacity ${isDisabled ? 'opacity-50 pointer-events-none' : ''}`}>
            <div className="mt-6">
                <h4 className={`text-lg font-semibold ${isDisabled ? 'text-gray-500' : 'text-jtc-very-dark-grey'}`}>
                    Upload Documents & Media
                </h4>
                <p className={`text-sm mb-4 ${isDisabled ? 'text-gray-400' : 'text-gray-500'}`}>
                    Add documents and media that your assistant will use to learn. These sources form
                    the basis for your assistant's responses.
                    <br />
                    Manage your media library via the{" "}
                    <a
                        href="https://www.videoindexer.ai/media/library"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                    >
                        Video Indexer portal.
                    </a>
                </p>
                <div
                    className={`mt-1 flex items-center justify-center px-6 pt-5 pb-6 h-48 border-2 border-dashed rounded-md ${isDragOver ? 'border-jtc-blue' : 'border-jtc-grey'
                        }`}
                    onDragOver={onDragOver}
                    onDragLeave={onDragLeave}
                    onDrop={onDrop}
                >
                    <div className="space-y-1 text-center">
                        <AiOutlineFileAdd
                            className={`mx-auto h-12 w-12 ${uploadedFiles.length > 0 ? 'text-jtc-blue' : 'text-gray-400'
                                }`}
                        />
                        {uploadedFiles.length > 0 ? (
                            <>
                                <p className="text-sm text-gray-600">
                                    {uploadedFiles.length} file
                                    {uploadedFiles.length > 1 ? 's' : ''} selected
                                    {documentCount > 0 && ` (${documentCount} document${documentCount > 1 ? 's' : ''})`}
                                    {videoCount > 0 && ` (${videoCount} video${videoCount > 1 ? 's' : ''})`}
                                </p>
                                <div className="flex text-sm text-gray-600 justify-center">
                                    <span>Drag and drop files here or</span>
                                    <label
                                        htmlFor="file-upload"
                                        className="relative cursor-pointer bg-white rounded-md font-medium text-jtc-blue hover:text-jtc-blue focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-jtc-blue ml-1"
                                    >
                                        <span>Browse for more</span>
                                        <input
                                            id="file-upload"
                                            name="file-upload"
                                            type="file"
                                            className="sr-only"
                                            accept={KNOWLEDGE_ACCEPT}
                                            multiple
                                            ref={fileInputRef}
                                            onChange={onFileChange}
                                        />
                                    </label>
                                </div>
                            </>
                        ) : (
                            <div className="flex text-sm text-gray-600 justify-center">
                                <label
                                    htmlFor="file-upload"
                                    className="relative cursor-pointer bg-white rounded-md font-medium text-jtc-blue hover:text-jtc-blue focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-jtc-blue"
                                >
                                    <span>Browse for files</span>
                                    <input
                                        id="file-upload"
                                        name="file-upload"
                                        type="file"
                                        className="sr-only"
                                        accept={KNOWLEDGE_ACCEPT}
                                        multiple
                                        ref={fileInputRef}
                                        onChange={onFileChange}
                                    />
                                </label>
                                <p className="pl-1">or drag and drop</p>
                            </div>
                        )}
                        <p className="text-xs text-gray-500">
                            Documents ({DOCUMENT_TYPES_LABEL}) up to {documentSizeLimit}MB, MP4 videos up to {videoSizeLimitGb}GB
                        </p>
                    </div>
                </div>
            </div>

            {/* Uploaded Files List */}
            <div className="mt-4 space-y-2">
                <div className="text-sm text-gray-500 text-right mb-1">
                    {uploadedFiles.length} / {fileLimit}
                </div>
                {uploadedFiles.map((uploadedFile, index) => (
                    <div
                        key={index}
                        className="flex items-center justify-between text-sm text-gray-600"
                    >
                        <div className="flex items-center">
                            <span
                                className="mr-2"
                                style={{
                                    width: '20px',
                                    display: 'inline-block',
                                    textAlign: 'right'
                                }}
                            >
                                {index + 1}.
                            </span>
                            {uploadedFile.type === 'document' ? (
                                <RiFileTextFill className="text-jtc-blue mr-2" />
                            ) : (
                                <MdVideoLibrary className="text-jtc-purple mr-2" />
                            )}
                            <span>{uploadedFile.file.name}</span>
                            <span className="ml-2 text-gray-400">
                                ({formatFileSize(uploadedFile.file.size)})
                            </span>
                        </div>
                        <button
                            type="button"
                            onClick={() => onRemoveFile(index)}
                            className="text-red-500 hover:text-red-700 ml-2"
                        >
                            <MdClose />
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
};
